import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useForm } from "@/hooks/useForm";
import { useTripEventPolicies } from "@/hooks/queries/useTripEventPolicies";
import {
  updateTripEventPolicy,
  type TripEventPolicy,
} from "@/lib/api/trip-event-policies";
import { queryKeys } from "@/lib/query-keys";
import { getTripEventLabel } from "@/lib/trip-labels";
import { AdaptiveSheet } from "@/components/ui/adaptive-sheet";
import { ModalBody, ModalFooter, ModalHeader } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Surface } from "@/components/ui/surface";
import { Label, PageHeading, Text } from "@/components/ui/text";
import { HStack } from "@/components/ui/h-stack";
import { VStack } from "@/components/ui/v-stack";
import { FormLabel } from "@/components/ui/form-label";
import { Skeleton } from "@/components/ui/skeleton";
import { Icon } from "@/components/ui/icons";
import { useToast } from "@/components/ui/toast";

const policySchema = z.object({
  expectedMinutes: z.number().int().min(1),
  escalateMinutes: z.number().int().min(1).nullable(),
});

function AdminTripEventPoliciesCatalogPage() {
  const { t } = useTranslation("admin.catalogs.tripEventPolicies");
  const { t: tTrips } = useTranslation("trips");
  const { data: policies, isLoading, isError } = useTripEventPolicies();

  const [editing, setEditing] = useState<TripEventPolicy | null>(null);

  return (
    <VStack gap="xl" data-testid="admin-trip-event-policies-page">
      <VStack gap="xs">
        <PageHeading>{t("title")}</PageHeading>
        <Text size="sm" color="muted">
          {t("description")}
        </Text>
      </VStack>

      {isLoading && (
        <VStack gap="md">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full" rounded="lg" />
          ))}
        </VStack>
      )}

      {isError && (
        <Text size="sm" color="danger">
          {t("failedToLoad")}
        </Text>
      )}

      {policies && policies.length > 0 && (
        <VStack gap="md">
          {policies.map((policy) => (
            <Surface
              key={policy.event}
              bg="container"
              border="default"
              rounded="lg"
              padding="md"
              data-testid={`tep-row-${policy.event}`}
            >
              <HStack justify="between" gap="sm">
                <VStack gap="none" className="min-w-0">
                  <Text weight="medium" truncate>
                    {getTripEventLabel(policy.event, tTrips)}
                  </Text>
                  <Text size="xs" color="muted">
                    {t("expectedSummary", { minutes: policy.expected_minutes })}
                  </Text>
                </VStack>
                <Button
                  type="button"
                  color="neutral"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(policy)}
                  data-testid={`tep-edit-${policy.event}`}
                >
                  {t("edit")}
                </Button>
              </HStack>
            </Surface>
          ))}
        </VStack>
      )}

      {editing && (
        <TripEventPolicyFormModal
          policy={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </VStack>
  );
}

function TripEventPolicyFormModal({
  policy,
  onClose,
}: {
  policy: TripEventPolicy;
  onClose: () => void;
}) {
  const { t } = useTranslation("admin.catalogs.tripEventPolicies");
  const { t: tTrips } = useTranslation("trips");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const mutation = useMutation({
    mutationFn: (fields: {
      expectedMinutes: number;
      escalateMinutes: number | null;
    }) =>
      updateTripEventPolicy(policy.event, {
        expected_minutes: fields.expectedMinutes,
        escalate_minutes: fields.escalateMinutes,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tripEventPolicies(),
      });
      onClose();
    },
    onError: () => addToast(t("failedToSave"), "error"),
  });

  const form = useForm({
    schema: policySchema,
    defaults: {
      expectedMinutes: policy.expected_minutes,
      escalateMinutes: policy.escalate_minutes,
    },
    onSubmit: async (data) => {
      await mutation.mutateAsync(data);
    },
  });

  return (
    <AdaptiveSheet
      open
      onDismiss={() => {
        if (!form.isSubmitting) onClose();
      }}
      onSubmit={() => void form.submitSafe()}
      size="md"
    >
      <ModalHeader>
        <Text>{getTripEventLabel(policy.event, tTrips)}</Text>
        <IconButton
          type="button"
          onClick={onClose}
          aria-label={tCommon("close")}
          title={tCommon("close")}
        >
          <Icon.X size="md" />
        </IconButton>
      </ModalHeader>
      <ModalBody className="space-y-4">
        <VStack gap="xs">
          <FormLabel>{t("fields.expectedLabel")}</FormLabel>
          <Input
            type="number"
            value={String(form.fields.expectedMinutes)}
            onChange={(e) =>
              form.setField("expectedMinutes", Number(e.target.value) || 0)
            }
            disabled={form.isSubmitting}
            data-testid="tep-expected-minutes"
          />
          <Label size="xs" color="muted">
            {t("fields.expectedHelper")}
          </Label>
        </VStack>

        <VStack gap="xs">
          <FormLabel>{t("fields.escalateLabel")}</FormLabel>
          <Input
            type="number"
            value={
              form.fields.escalateMinutes == null
                ? ""
                : String(form.fields.escalateMinutes)
            }
            onChange={(e) => {
              const raw = e.target.value.trim();
              form.setField(
                "escalateMinutes",
                raw === "" ? null : Number(raw) || 0,
              );
            }}
            disabled={form.isSubmitting}
            data-testid="tep-escalate-minutes"
          />
          <Label size="xs" color="muted">
            {t("fields.escalateHelper")}
          </Label>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button
          type="button"
          color="neutral"
          variant="outline"
          onClick={onClose}
          disabled={form.isSubmitting}
        >
          {tCommon("cancel")}
        </Button>
        <Button
          type="button"
          onClick={() => void form.submitSafe()}
          disabled={form.isSubmitting || !form.isValid}
          data-testid="tep-save"
        >
          {form.isSubmitting ? <Icon.Spinner /> : null}
          {tCommon("save")}
        </Button>
      </ModalFooter>
    </AdaptiveSheet>
  );
}

export const Route = createFileRoute(
  "/_app/admin/catalogs/trip-event-policies",
)({
  component: AdminTripEventPoliciesCatalogPage,
});
